<script setup lang="ts" generic="T extends ImageBlock | GalleryImageItem">
import { AlignHorizontalCenters, AlignLeftBox, AlignRightBox } from '@iconoir/vue'
import { Pencil } from 'lucide-vue-next'
import type { Component } from 'vue'

import EmailImageMaskPicker from './EmailImageMaskPicker.vue'
import EmailPanelSection from '../EmailPanelSection.vue'
import ColorPickerWithPresets from '../ColorPickerWithPresets.vue'
import EmailLinkInput from '../ui/EmailLinkInput.vue'

import { isAnimatedImage } from '../../utils/imageMask'
import type { Alignment, GalleryImageItem, ImageBlock, ImageMaskShape } from '../../types'

const { t } = useI18n()

const props = defineProps<{
  block: T
  isMaskBusy?: boolean
  colorPresets?: { color: string; label: string }[]
}>()

const emit = defineEmits<{
  (e: 'update:block', block: T): void
  (e: 'mask-change', mask: ImageMaskShape): void
  (e: 'replace'): void
}>()

const fullBlock = computed<ImageBlock | null>(() => ('width' in props.block ? (props.block as ImageBlock) : null))
const isAnimated = computed(() => isAnimatedImage(fullBlock.value?.originalSrc ?? fullBlock.value?.src))

const patchBlock = (patch: Partial<ImageBlock & GalleryImageItem>) => {
  emit('update:block', { ...props.block, ...patch } as T)
}

const alignments: { value: Alignment; icon: Component }[] = [
  { value: 'left', icon: AlignLeftBox },
  { value: 'center', icon: AlignHorizontalCenters },
  { value: 'right', icon: AlignRightBox }
]

const widthValue = computed({
  get: () => [fullBlock.value?.width ?? 100],
  set: ([next]) => {
    if (!fullBlock.value) return
    patchBlock({ width: Math.max(25, Math.min(100, next)) })
  }
})

const replaceLabel = computed(() =>
  props.block.src
    ? t('pages.email.emailTemplateCreatePage.changeImage')
    : t('pages.email.emailTemplateCreatePage.gallery.pickImage')
)
</script>

<template>
  <Teleport to="#email-settings-panel" defer>
    <div class="space-y-8 px-4 pb-6 pt-4">
      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.content')">
        <Button type="button" variant="outline" class="w-full" @click="emit('replace')">
          <Pencil />
          {{ replaceLabel }}
        </Button>
      </EmailPanelSection>

      <EmailPanelSection v-if="fullBlock" :title="t('pages.email.emailTemplateCreatePage.panel.blockBackground')">
        <ColorPickerWithPresets
          :model-value="fullBlock.blockBackgroundColor ?? 'ffffff'"
          :presets="colorPresets ?? []"
          @update:model-value="(v) => patchBlock({ blockBackgroundColor: v })"
        />
      </EmailPanelSection>

      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.imageAlt')">
        <Input
          :model-value="block.alt"
          :placeholder="t('pages.email.emailTemplateCreatePage.imageAltPlaceholder')"
          @update:model-value="(v) => patchBlock({ alt: String(v) })"
        />
      </EmailPanelSection>

      <EmailPanelSection v-if="fullBlock" :title="t('pages.email.emailTemplateCreatePage.panel.globalAlignment')">
        <Tabs :model-value="fullBlock.align" @update:model-value="(v) => patchBlock({ align: v as Alignment })">
          <TabsList class="w-full">
            <TabsTrigger v-for="opt in alignments" :key="opt.value" :value="opt.value" class="flex-1">
              <component :is="opt.icon" />
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </EmailPanelSection>

      <EmailPanelSection v-if="fullBlock" :title="t('pages.email.emailTemplateCreatePage.panel.imageWidth')">
        <div class="space-y-2">
          <div class="flex items-center justify-between">
            <Label class="text-xs font-medium text-foreground">
              {{ t('pages.email.emailTemplateCreatePage.panel.imageWidth') }}
            </Label>
            <span class="text-[11px] tabular-nums text-muted-foreground">{{ fullBlock.width }}%</span>
          </div>
          <Slider v-model="widthValue" :min="25" :max="100" :step="5" />
        </div>
      </EmailPanelSection>

      <EmailPanelSection v-if="fullBlock" :title="t('pages.email.emailTemplateCreatePage.panel.imageShape')">
        <EmailImageMaskPicker
          :value="fullBlock.mask ?? 'none'"
          :disabled="isMaskBusy || isAnimated"
          @update:value="emit('mask-change', $event)"
        />
        <p v-if="isAnimated" class="text-xs text-muted-foreground">
          {{ t('pages.email.emailTemplateCreatePage.panel.imageShapeAnimated') }}
        </p>
      </EmailPanelSection>

      <EmailPanelSection :title="t('pages.email.emailTemplateCreatePage.panel.link')">
        <EmailLinkInput
          stacked
          class="w-full"
          :model-value="block.link"
          @update:model-value="(v) => patchBlock({ link: v })"
        />
      </EmailPanelSection>

      <slot name="footer" />
    </div>
  </Teleport>
</template>
